import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Input, Message, Modal } from '@arco-design/web-react';
import { IconCheck, IconClockCircle, IconDown, IconSearch, IconUser } from '@arco-design/web-react/icon';

import { ConfigContext } from '@/components/ConfigProvider';
import { IconTenant } from '@/components/Icon/IconTenant';
import { getConsoleSubCenterOverviewPath, getEffectiveSubCenter } from '@/hooks/useMenu/subCenter';
import useTenants from '@/hooks/useTenants';
import { getDcAndValByTenant } from '@/layout/servies';
import { cs } from '@/utils/className';
import { formatIsoDate } from '@/utils/date-tools';

import styles from './index.module.less';
import { getList } from './service';

let timer = null;

interface TenantBarProps {
  /** 仅展示当前租户，不可切换（如商品页等特殊场景） */
  readOnly?: boolean;
}

function Index({ readOnly = false }: TenantBarProps) {
  const { t } = useTranslation();
  const params = useParams();
  const [data, setData] = useState([]);
  const { tenant, tenantList } = useTenants();
  const { setTenantCluster, setTenantClusters, tenantClusters } = useContext(ConfigContext);
  const tenantClustersRef = useRef(tenantClusters);
  tenantClustersRef.current = tenantClusters;
  const navigate = useNavigate();
  const [modalVisible, setModalVisible] = useState(false);
  const [search, setSearch] = useState('');

  const filteredData = search
    ? data?.filter(
        (i: any) =>
          (i.displayName || '').toLowerCase().includes(search.toLowerCase()) || i.name.toLowerCase().includes(search.toLowerCase()),
      )
    : data;

  const getData = useCallback((name) => {
    getList(name)
      .then((res: any) => {
        if (Array.isArray(res)) {
          setData(res);
        }
      })
      .catch((e) => {
        console.log(e);
      });
  }, []);

  // 获取当前租户的集群列表；内容未变时跳过 setState，打断无效更新环。
  const getTenantClusters0 = useCallback(
    async (go = true) => {
      if (!tenant) return;
      const { data: nextData, value } = await getDcAndValByTenant({ tenant });
      const prev = tenantClustersRef.current;
      const unchanged =
        Array.isArray(prev) &&
        Array.isArray(nextData) &&
        prev.length === nextData.length &&
        prev.every((item, index) => item?.name === nextData[index]?.name && item?.live === nextData[index]?.live);
      if (!unchanged) {
        setTenantClusters(nextData);
      }
      if (go) {
        // 无可用数据中心时不要 navigate，避免把 undefined 写进 URL 变成字面量 "undefined"
        if (!value || !params.tenant || !params.cluster) return;
        const newPath = window.location.pathname
          .replace(`/console/${params.tenant}/`, `/console/${tenant}/`)
          .replace(`/cluster/${params.cluster}`, `/cluster/${value}`);
        if (newPath !== window.location.pathname) {
          navigate(newPath, { replace: true });
        }
        setTenantCluster(value, tenant);
      }
    },
    [navigate, params.cluster, params.tenant, setTenantCluster, setTenantClusters, tenant],
  );

  useEffect(() => {
    getData('');
  }, [getData]);

  // 租户变化时同步集群列表与轮询；内容未变时 getTenantClusters0 内部会跳过 setState。
  useEffect(() => {
    getTenantClusters0(false);
  }, [tenant, getTenantClusters0]);

  useEffect(() => {
    if (tenant !== params?.tenant && params?.tenant && tenant) {
      localStorage.setItem('tenant', JSON.stringify(tenant));
      getTenantClusters0();
    }
    timer && clearInterval(timer);
    timer = setInterval(() => {
      getTenantClusters0(false);
    }, 10 * 1000);
    return () => {
      timer && clearInterval(timer);
    };
  }, [tenant, params?.tenant, getTenantClusters0]);

  const changeTenant = async (nextTenant) => {
    if (params.tenant === nextTenant) {
      Message.warning(t('components:already.the.current.tenant.space'));
      return;
    }
    localStorage.setItem('tenant', JSON.stringify(nextTenant));
    const { value: newCluster } = await getDcAndValByTenant({ tenant: nextTenant, useCache: false });
    const clusterPart = newCluster || ':cluster';

    // 如果当前不在 console 租户页面下，保留当前页面路径
    if (!window.location.pathname.startsWith('/console')) {
      window.location.reload();
      return;
    }

    // 切换租户后进入当前子中心总览页（AI 中心暂用模型广场）
    const centerKey = getEffectiveSubCenter(window.location.pathname);
    window.location.replace(getConsoleSubCenterOverviewPath(nextTenant, clusterPart, centerKey));
  };

  const handleItemClick = (name: string) => {
    changeTenant(name);
    setModalVisible(false);
    setSearch('');
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSearch('');
  };

  const handleModalOpen = () => {
    setSearch('');
    getData('');
    setModalVisible(true);
  };

  const currentItem = (data as any[])?.find((i: any) => i.name === tenant);
  const displayValue = currentItem?.displayName || currentItem?.name || tenant;

  if (readOnly) {
    return (
      <div className={cs(styles['select-trigger'], styles['select-trigger-readonly'])}>
        <span className={styles['select-trigger-prefix']}>
          <IconTenant style={{ fontSize: 14 }} />
          {t('common:tenant.with.colon')}
        </span>
        <span className={styles['select-trigger-value']}>{displayValue || t('common:workspace.placeholder')}</span>
      </div>
    );
  }

  return (
    <>
      <div className={styles['select-trigger']} onClick={handleModalOpen}>
        <span className={styles['select-trigger-prefix']}>
          <IconTenant style={{ fontSize: 14 }} />
          {t('common:tenant.with.colon')}
        </span>
        {tenantList?.length > 0 ? (
          <span className={styles['select-trigger-value']}>{displayValue || t('common:workspace.placeholder')}</span>
        ) : (
          <span className={styles['select-trigger-placeholder']}>
            {t('common:no.available.tenant.spaces.please.contact.the.administrator.to.add')}
          </span>
        )}
        <IconDown className={styles['select-trigger-arrow']} />
      </div>
      {/* 弹窗选择租户 */}
      <Modal
        title={t('components:select.tenant.space')}
        visible={modalVisible}
        footer={null}
        onCancel={handleModalClose}
        style={{ width: '40vw' }}
        maskClosable={true}
      >
        <Input
          className={styles['modal-search']}
          prefix={<IconSearch />}
          placeholder={t('common:search')}
          value={search}
          onChange={setSearch}
          allowClear
        />
        <div className={styles['modal-list']}>
          {filteredData?.length === 0 ? (
            <div className={styles['modal-empty']}>
              {data?.length === 0
                ? t('common:no.available.tenant.spaces.please.contact.the.administrator.to.add')
                : t('components:no.matching.result')}
            </div>
          ) : (
            (filteredData as any[])?.map((item: any) => {
              const isActive = item.name === tenant;
              const itemClass = [styles['modal-item'], isActive ? styles['modal-item-active'] : ''].filter(Boolean).join(' ');

              return (
                <div key={item.name} className={itemClass} onClick={() => handleItemClick(item.name)} title={item.displayName}>
                  <div className={styles['modal-item-icon']}>
                    <IconTenant style={{ fontSize: 20 }} />
                  </div>
                  <div className={styles['modal-item-info']}>
                    <div className={styles['modal-item-name']}>
                      {item.displayName || '-'}
                      <span className={styles['modal-item-desc']}>({item.name})</span>
                    </div>

                    <div className={styles['modal-item-meta']}>
                      {item.ownerName && (
                        <span className={styles['modal-item-meta-tag']}>
                          <IconUser />
                          {t('common:owner.with.colon')}
                          {item.ownerName}
                        </span>
                      )}
                      {item.creatorName && (
                        <span className={styles['modal-item-meta-tag']}>
                          <IconUser />
                          {t('common:creator.with.colon')}
                          {item.creatorName}
                        </span>
                      )}
                      {item.createTime && (
                        <span className={styles['modal-item-meta-tag']}>
                          <IconClockCircle />
                          {t('common:created.time.with.colon')}
                          {formatIsoDate(item.createTime, 'YYYY-MM-DD HH:mm')}
                        </span>
                      )}
                    </div>
                  </div>
                  {isActive && <IconCheck className={styles['modal-item-check']} />}
                </div>
              );
            })
          )}
        </div>
      </Modal>
    </>
  );
}

export default Index;
